import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import MapIcon from '@mui/icons-material/Map';
import ArticleIcon from '@mui/icons-material/Article';

import { useSmoothScrollTo } from '../hooks/useSmoothScrollTo';

import { GREEN, GREEN_HOVER, GREEN50, CHARCOAL, BORDER, MUTED, labelMono, bodyFont } from '../../theme/tokens2';

// ─── Resources ────────────────────────────────────────────────────────────────
// The groups and links in the "Resources" menu, used by both the desktop
// dropdown and the mobile menu. To add a link, add a line to `items`.
// Items with `comingSoon: true` are shown greyed out, with a "Coming Soon" tag.
const RESOURCE_GROUPS = [
  {
    title: 'Showroom Maps',
    icon:  MapIcon,
    items: [
      { label: 'Singapore',  href: '/showrooms', comingSoon: false },
      { label: 'Copenhagen', href: null,         comingSoon: true  },
      { label: 'Foshan',     href: null,         comingSoon: true  },
    ],
  },
  {
    title: 'Guides & Resources',
    icon:  ArticleIcon,
    items: [
      { label: 'Inspiration Accounts', href: '/socials', comingSoon: false },
      { label: 'Fengshui Guide',       href: null,       comingSoon: true  },
    ],
  },
];

// ─── Dropdown link (desktop) ──────────────────────────────────────────────────
function DropdownLink({ to, onClick, children }) {
  return (
    <Box
      component={Link} to={to}
      onClick={onClick}
      sx={{
        ...bodyFont,
        display: 'block',
        px: 1, py: '8px', mx: -1,
        fontSize: '0.875rem',
        color: CHARCOAL,
        textDecoration: 'none',
        transition: 'color 0.15s ease, background-color 0.15s ease',
        '&:hover': { color: GREEN, backgroundColor: GREEN50 },
      }}
    >
      {children}
    </Box>
  );
}

// ─── Dropdown link (mobile) ───────────────────────────────────────────────────
function DropdownLinkMobile({ to, onClick, children }) {
  return (
    <Box
      component={Link} to={to}
      onClick={onClick}
      sx={{
        ...bodyFont,
        display: 'block',
        fontSize: '0.875rem', color: MUTED, py: '6px',
        textDecoration: 'none',
      }}
    >
      {children}
    </Box>
  );
}

// ─── "Coming Soon" item (desktop and mobile) ──────────────────────────────────
function ComingSoonItem({ children }) {
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      px: 1, py: '8px', mx: -1,
      cursor: 'default',
    }}>
      <Typography sx={{ ...bodyFont, fontSize: '0.875rem', color: 'rgba(0,0,0,0.25)' }}>
        {children}
      </Typography>

      <Box sx={{
        ...labelMono,
        fontSize: '8px',
        color: 'rgba(0,0,0,0.25)',
        border: `1px solid ${BORDER}`,
        px: '6px', py: '2px',
      }}>
        Coming Soon
      </Box>
    </Box>
  );
}

// Small green heading above each group of links, e.g. "SHOWROOM MAPS".
// (labelMono turns the title into capital letters.)
function ResourceGroupHeading({ group, iconSize, sx }) {
  const Icon = group.icon;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ...sx }}>
      <Icon sx={{ fontSize: iconSize, color: GREEN }} />
      <Typography sx={{ ...labelMono, color: GREEN, fontSize: '9px' }}>{group.title}</Typography>
    </Box>
  );
}

// ─── Resources dropdown (desktop) ─────────────────────────────────────────────
function ResourcesDropdown() {
  const [open, setOpen] = useState(false);
  const ref             = useRef(null);

  // Close the dropdown when the user clicks anywhere outside it
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const lastIndex = RESOURCE_GROUPS.length - 1;

  return (
    <Box ref={ref} sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
      {/* Button that opens and closes the dropdown */}
      <Box
        component="button"
        onClick={() => setOpen(o => !o)}
        sx={{
          ...labelMono,
          display: 'inline-flex', alignItems: 'center', gap: 0.75,
          color: MUTED,
          background: 'none', border: 'none', cursor: 'pointer', p: 0,
          transition: 'color 0.2s ease',
          '&:hover': { color: CHARCOAL },
        }}
      >
        Resources
        <KeyboardArrowDownIcon sx={{
          fontSize: '0.75rem',
          transition: 'transform 0.2s ease',
          transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
        }} />
      </Box>

      {/* Dropdown panel */}
      {open && (
        <Box sx={{
          position: 'absolute', top: '100%', right: 0, mt: 1.5,
          width: 300,
          backgroundColor: '#fff',
          border: `1px solid ${BORDER}`,
          boxShadow: '0 4px 24px rgba(0,0,0,0.10)',
          zIndex: 50,
        }}>
          {RESOURCE_GROUPS.map((group, i) => (
            <React.Fragment key={group.title}>
              {/* Thin line between groups */}
              {i > 0 && <Box sx={{ height: '1px', backgroundColor: BORDER, mx: 2 }} />}

              <Box sx={{ px: 2, pt: i === 0 ? 2 : 1.5, pb: i === lastIndex ? 2 : 1 }}>
                <ResourceGroupHeading group={group} iconSize="0.75rem" sx={{ mb: 1 }} />
                {group.items.map(item => item.comingSoon ? (
                  <ComingSoonItem key={item.label}>{item.label}</ComingSoonItem>
                ) : (
                  <DropdownLink key={item.label} to={item.href} onClick={() => setOpen(false)}>
                    {item.label}
                  </DropdownLink>
                ))}
              </Box>
            </React.Fragment>
          ))}
        </Box>
      )}
    </Box>
  );
}

// ─── Resources dropdown (mobile) ──────────────────────────────────────────────
// Opens and closes in place inside the mobile menu.
// onClose closes the whole mobile menu after a link is tapped.
function ResourcesDropdownMobile({ onClose }) {
  const [open, setOpen] = useState(false);

  return (
    <Box>
      {/* Button row — same visual level as How It Works */}
      <Box
        component="button"
        onClick={() => setOpen(o => !o)}
        sx={{
          ...labelMono, color: MUTED,
          width: '100%', background: 'none', border: 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          py: '14px', px: 0,
          transition: 'color 0.2s ease',
        }}
      >
        Resources
        <KeyboardArrowDownIcon sx={{
          fontSize: '0.75rem',
          transition: 'transform 0.2s ease',
          transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          color: MUTED,
        }} />
      </Box>

      {/* Expandable links */}
      <Box sx={{
        overflow: 'hidden',
        maxHeight: open ? '400px' : '0px',
        transition: 'max-height 0.3s cubic-bezier(0.22,1,0.36,1)',
      }}>
        {/* Indented with a line on the left, to show these links belong to Resources */}
        <Box sx={{ pl: 2, pb: 2, borderLeft: `2px solid ${BORDER}`, ml: 0.5 }}>
          {RESOURCE_GROUPS.map(group => (
            <React.Fragment key={group.title}>
              <ResourceGroupHeading group={group} iconSize="0.7rem" sx={{ pt: 2, pb: 1 }} />
              {group.items.map(item => item.comingSoon ? (
                <ComingSoonItem key={item.label}>{item.label}</ComingSoonItem>
              ) : (
                <DropdownLinkMobile key={item.label} to={item.href} onClick={onClose}>
                  {item.label}
                </DropdownLinkMobile>
              ))}
            </React.Fragment>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

// ─── Header ───────────────────────────────────────────────────────────────────
export default function Header() {
  const [scrolled,   setScrolled]   = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [navVisible, setNavVisible] = useState(false);

  const navigate = useNavigate();
  const scrollTo = useSmoothScrollTo();

  // Close the mobile menu and scroll to a section
  const handleSectionNav = (id) => {
    setMobileOpen(false);
    scrollTo(id);
  };

  // Entrance animation
  useEffect(() => {
    const t = setTimeout(() => setNavVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  // Add a background to the header once the page is scrolled
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <Box
      component="nav"
      sx={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1200,
        opacity:   navVisible ? 1 : 0,
        transform: navVisible ? 'translateY(0)' : 'translateY(-8px)',
        backgroundColor: scrolled ? 'rgba(255,255,255,0.95)' : 'transparent',
        backdropFilter:  scrolled ? 'blur(12px)' : 'none',
        borderBottom: `1px solid ${scrolled ? BORDER : 'transparent'}`,
        transition: 'opacity 0.5s ease, transform 0.5s ease, background-color 0.3s ease, border-color 0.3s ease',
      }}
    >
      {/* ── Main bar ── */}
      <Box sx={{
        maxWidth: 1400, mx: 'auto',
        px: { xs: 3, md: 6 },
        height: 64,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>

        {/* Company name — goes to the top of the homepage */}
        <Box
          component="button"
          onClick={() => {
            if (window.location.pathname === '/') {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
              navigate('/');
            }
          }}
          sx={{ background: 'none', border: 'none', cursor: 'pointer', p: 0 }}
        >
          <Typography sx={{ ...labelMono, color: CHARCOAL }}>Savvy Renovator</Typography>
        </Box>

        {/* Desktop links (900px and wider) */}
        <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 5 }}>

          <ResourcesDropdown />

          {/* Sign In */}
          <Box
            component="button"
            onClick={() => navigate('/signin')}
            sx={{
              ...labelMono,
              color: MUTED,
              background: 'none', border: 'none', cursor: 'pointer', p: 0,
              transition: 'color 0.2s ease',
              '&:hover': { color: CHARCOAL },
            }}
          >
            Sign In
          </Box>

          {/* Upload Floor Plan */}
          <Box
            component="button"
            onClick={() => handleSectionNav('final-cta')}
            sx={{
              ...labelMono,
              color: '#fff',
              backgroundColor: GREEN,
              border: 'none', cursor: 'pointer',
              px: '20px', py: '10px',
              transition: 'background-color 0.2s ease',
              '&:hover': { backgroundColor: GREEN_HOVER },
            }}
          >
            Upload Floor Plan
          </Box>
        </Box>

        {/* Mobile menu button */}
        <IconButton
          onClick={() => setMobileOpen(o => !o)}
          sx={{ display: { xs: 'flex', md: 'none' }, color: CHARCOAL }}
          aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
        >
          {mobileOpen ? <CloseIcon sx={{ fontSize: 20 }} /> : <MenuIcon sx={{ fontSize: 20 }} />}
        </IconButton>
      </Box>

      {/* ── Mobile menu (narrower than 900px) ── */}
      <Box sx={{
        display: { xs: 'block', md: 'none' },
        overflow: 'hidden',
        maxHeight: mobileOpen ? '100vh' : '0px',
        opacity: mobileOpen ? 1 : 0,
        backgroundColor: '#fff',
        borderBottom: mobileOpen ? `1px solid ${BORDER}` : 'none',
        transition: 'max-height 0.35s cubic-bezier(0.22,1,0.36,1), opacity 0.25s ease',
      }}>
        <Box sx={{ px: 3, py: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>

          {/* All-in-One Planning — scrolls to section 03 */}
          <Box
            component="button"
            onClick={() => handleSectionNav('planning')}
            sx={{ ...labelMono, color: MUTED, background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', p: 0 }}
          >
            ALL-IN-ONE PLANNING
          </Box>

          <ResourcesDropdownMobile onClose={() => setMobileOpen(false)} />

          {/* Sign In */}
          <Box
            component="button"
            onClick={() => { setMobileOpen(false); navigate('/signin'); }}
            sx={{ ...labelMono, color: MUTED, background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', p: 0 }}
          >
            Sign In
          </Box>

          {/* Upload Floor Plan */}
          <Box
            component="button"
            onClick={() => handleSectionNav('final-cta')}
            sx={{
              ...labelMono, color: '#fff', backgroundColor: GREEN,
              border: 'none', cursor: 'pointer',
              width: '100%', textAlign: 'center',
              px: '20px', py: '12px',
              '&:hover': { backgroundColor: GREEN_HOVER },
            }}
          >
            Upload Floor Plan
          </Box>

        </Box>
      </Box>
    </Box>
  );
}